/**
 * AllInCostPreview — interactive preview of the redesigned "All-In Project Cost"
 * card, using Ellis Row's real numbers. Route: /all-in-cost-preview
 *
 * New model (owner-confirmed):
 *  - All-In Project Cost = total ESTIMATED cost (construction estimates + property),
 *    NOT money spent so far — so it's meaningful before the project starts.
 *  - Compared against the declared Budget → under / over chip.
 *  - Budget "dwindles": a progress bar shows Used (spent + property) vs Available.
 *  - Drag the slider to simulate logging payments and watch Available draw down.
 */
import React, { useState } from 'react';
import { Info, TrendingUp, Wallet, CheckCircle2, AlertTriangle } from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

// ---- Ellis Row real figures ----
const BUDGET = 1478535.62;          // declared total budget
const PROPERTY = 278655.00;         // acquisition (home+land) + closing — already sunk
const EST_CONSTRUCTION = 800161.33; // sum of phase estimated costs (phases 1-4)
const MIN_SPENT = 35442.80;         // construction actually spent to date

const usd = (n) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdShort = (n) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function AllInCostPreview() {
  const [spent, setSpent] = useState(MIN_SPENT); // construction spent (interactive)

  const allInEstimated = EST_CONSTRUCTION + PROPERTY; // full projected cost
  const used = PROPERTY + spent;                      // real money out so far
  const available = BUDGET - used;                    // dwindling budget
  const usedPct = Math.min(100, (used / BUDGET) * 100);

  const underBudget = allInEstimated <= BUDGET;
  const variance = Math.abs(BUDGET - allInEstimated);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="text-center text-[11px] font-semibold text-amber-900 bg-amber-300 py-1.5 px-4">
        PREVIEW · redesigned All-In Project Cost card · Ellis Row sample data
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">All-In Project Cost — redesigned</h1>
          <p className="text-sm text-slate-500 mt-1">
            Now leads with your <b>projected total</b> and tracks your budget drawing down as you spend.
          </p>
        </div>

        {/* ============ THE NEW CARD (dark, matches Budget &amp; Progress styling) ============ */}
        <div className="rounded-3xl p-6 text-white shadow-xl" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }} data-testid="all-in-card">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-cyan-200 text-xs font-semibold uppercase tracking-wide">All-In Project Cost</p>
                <Info className="w-3.5 h-3.5 text-cyan-300" />
              </div>
              <p className="text-3xl font-extrabold mt-1" data-testid="all-in-estimated">{usd(allInEstimated)}</p>
              <p className="text-[11px] text-cyan-200 mt-1">Estimated total · construction estimates + property</p>
            </div>
            <div className="text-right">
              <p className="text-cyan-300 text-[11px]">Budget</p>
              <p className="text-lg font-bold">{usdShort(BUDGET)}</p>
              <span
                className={`inline-flex items-center gap-1 mt-1 text-[11px] font-bold px-2 py-1 rounded-full ${underBudget ? 'bg-emerald-500/25 text-emerald-300' : 'bg-red-500/25 text-red-300'}`}
                data-testid="budget-variance-chip"
              >
                {underBudget ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {usdShort(variance)} {underBudget ? 'under budget' : 'over budget'}
              </span>
            </div>
          </div>

          {/* Dwindling budget bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-[11px] text-cyan-200 mb-1.5">
              <span>Budget used</span>
              <span>{usedPct.toFixed(1)}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/15 overflow-hidden">
              <div className="h-3 rounded-full transition-all duration-300" style={{ width: `${usedPct}%`, background: GREEN }} data-testid="budget-used-bar" />
            </div>
          </div>

          {/* 3 stat tiles */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-[10px] text-cyan-200 uppercase tracking-wide">Spent to date</p>
              <p className="text-base font-bold mt-1" data-testid="spent-value">{usdShort(spent)}</p>
              <p className="text-[10px] text-cyan-300/80">construction</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-[10px] text-cyan-200 uppercase tracking-wide">Property</p>
              <p className="text-base font-bold mt-1">{usdShort(PROPERTY)}</p>
              <p className="text-[10px] text-cyan-300/80">land + closing</p>
            </div>
            <div className="bg-emerald-500/20 rounded-xl p-3 border border-emerald-400/30">
              <p className="text-[10px] text-emerald-200 uppercase tracking-wide">Available</p>
              <p className="text-base font-bold mt-1 text-emerald-300" data-testid="available-value">{usdShort(available)}</p>
              <p className="text-[10px] text-emerald-300/80">left to spend</p>
            </div>
          </div>
        </div>

        {/* ============ interactive simulator ============ */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: GREEN }} /> Simulate logging payments</p>
          <p className="text-xs text-slate-500 mt-1">Drag to spend more on construction and watch <b>Available</b> draw down and the bar fill.</p>
          <input
            type="range"
            min={MIN_SPENT}
            max={EST_CONSTRUCTION}
            step={1000}
            value={spent}
            onChange={(e) => setSpent(parseFloat(e.target.value))}
            className="w-full mt-4 accent-emerald-600"
            data-testid="spend-slider"
          />
          <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
            <span>{usdShort(MIN_SPENT)} (today)</span>
            <span>{usdShort(EST_CONSTRUCTION)} (fully built)</span>
          </div>
        </div>

        {/* ============ before / after explainer ============ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-xs font-bold text-red-700 uppercase tracking-wide">Old card</p>
            <p className="text-2xl font-extrabold text-red-900 mt-1">{usd(314097.80)}</p>
            <p className="text-xs text-red-700/80 mt-1">Showed sunk property + tiny construction spend as the headline — looked far smaller than your $800K of phases.</p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide">New card</p>
            <p className="text-2xl font-extrabold text-emerald-900 mt-1">{usd(allInEstimated)}</p>
            <p className="text-xs text-emerald-700/80 mt-1">Leads with the full projected cost, checked against your {usdShort(BUDGET)} budget, with spend &amp; available tracked below.</p>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 pb-6">
          Interactive preview · Ellis Row figures. Once you approve, this replaces the live All-In card on Budget &amp; Progress.
        </p>
      </div>
    </div>
  );
}
